import React, { useEffect } from "react";
import { View } from "react-native";
import Animated, {
  useAnimatedStyle,
  useDerivedValue,
  useSharedValue,
  withTiming,
  useAnimatedReaction,
  SharedValue,
  runOnJS,
  withSpring,
  cancelAnimation,
} from "react-native-reanimated";
import { Gesture, GestureDetector } from "react-native-gesture-handler";
import { withDecaySpring } from "./utils";
import { clamp, withRubberBandClamp } from "./utils/rubberBandClamp";
import { useVector } from "./utils/useVector";

const snapPoint = (
  value: number,
  velocity: number,
  points: readonly number[]
): number => {
  "worklet";
  const point = value + 0.25 * velocity;
  const deltas = points.map((p) => Math.abs(point - p));
  const minDelta = Math.min.apply(null, deltas);
  return points.filter((p) => Math.abs(point - p) === minDelta)[0];
};

type Dimensions = {
  height: number;
  width: number;
};

export type RenderItem<T> = (
  imageInfo: RenderItemInfo<T>
) => React.ReactElement | null;

export type RenderItemInfo<T> = {
  index: number;
  item: T;
  setImageDimensions: (imageDimensions: Dimensions) => void;
};

export type ItemRef = { reset: (animated: boolean) => void };
type Props<T> = {
  item: T;
  index: number;
  isFirst: boolean;
  isLast: boolean;
  translateY: SharedValue<number>;
  currentIndex: SharedValue<number>;
  renderItem: RenderItem<T>;
  width: number;
  height: number;
  length: number;
  emptySpaceHeight: number;
  doubleTapInterval: number;
  doubleTapScale: number;
  maxScale: number;
  pinchEnabled: boolean;
  disableTransitionOnScaledImage: boolean;
  hideAdjacentImagesOnScaledImage: boolean;
  loop: boolean;
  onScaleOrTranslationChange: (params: {
    scale: number;
    translateX: number;
    translateY: number;
  }) => void;
  setRef: (index: number, value: ItemRef) => void;
};

export const ResizableImage = <T extends any>({
  item,
  translateY,
  index,
  isFirst,
  isLast,
  currentIndex,
  renderItem,
  width,
  height,
  emptySpaceHeight,
  doubleTapScale,
  doubleTapInterval,
  maxScale,
  pinchEnabled,
  disableTransitionOnScaledImage,
  hideAdjacentImagesOnScaledImage,
  loop,
  length,
  onScaleOrTranslationChange,
  setRef,
}: Props<T>) => {
  "use no memo";
  const CENTER = {
    x: width / 2,
    y: height / 2,
  };

  const offset = useVector(0, 0);

  const scale = useSharedValue(1);

  const translation = useVector(0, 0);

  const origin = useVector(0, 0);

  const adjustedFocal = useVector(0, 0);

  const originalLayout = useVector(width, 0);
  const layout = useVector(width, 0);

  const isActive = useDerivedValue(
    () => currentIndex.value === index,
    [currentIndex]
  );

  useAnimatedReaction(
    () => ({
      scale: scale.value,
      translateX: translation.x.value,
      translateY: translation.y.value,
      offsetX: offset.x.value,
      offsetY: offset.y.value,
      height: layout.y.value,
      width: layout.x.value,
    }),
    (
      { scale, translateX, translateY, offsetX, offsetY, height, width },
      previous
    ) => {
      if (
        previous?.scale !== scale ||
        previous?.translateX !== translateX ||
        previous.translateY !== translateY
      ) {
        runOnJS(onScaleOrTranslationChange)({
          scale,
          translateY: 0.5 - (translateY + offsetY) / (height * scale * 0.9),
          translateX: 0.5 - (translateX + offsetX) / (width * scale * 0.9),
          // the 0.9 here is probably bc of a ratio that i missed
          // its there to make the translation more sensitive when zoomed in
        });
      }
    },
    []
  );

  const setAdjustedFocal = ({
    focalX,
    focalY,
  }: Record<"focalX" | "focalY", number>) => {
    "worklet";
    adjustedFocal.x.value = focalX - (CENTER.x + offset.x.value);
    adjustedFocal.y.value = focalY - (CENTER.y + offset.y.value);
  };

  const resetValues = (animated = true) => {
    "worklet";
    scale.value = animated ? withTiming(1) : 1;
    offset.x.value = animated ? withTiming(0) : 0;
    offset.y.value = animated ? withTiming(0) : 0;
    translation.x.value = animated ? withTiming(0) : 0;
    translation.y.value = animated ? withTiming(0) : 0;
  };

  const getEdgeX = () => {
    "worklet";
    const newWidth = scale.value * layout.x.value;

    const point = (newWidth - width) / 2;

    if (point < 0) {
      return [-0, 0];
    }

    return [-point, point];
  };

  const clampY = (value: number, newScale: number) => {
    "worklet";
    const newHeight = newScale * layout.y.value;
    const point = newHeight - height;

    if (newHeight < height) {
      return 0;
    }
    return clamp(value, -point, point);
  };

  const clampX = (value: number, newScale: number) => {
    "worklet";
    const newWidth = newScale * layout.x.value;
    const point = newWidth - width;

    if (newWidth < width) {
      return 0;
    }
    return clamp(value, -point, point);
  };

  const getEdgeY = () => {
    "worklet";
    const newHeight = scale.value * layout.y.value;

    const point = (newHeight - height) / 2;

    return [-point, point];
  };

  const onStart = () => {
    "worklet";
    cancelAnimation(translateY);

    offset.x.value = offset.x.value + translation.x.value;
    offset.y.value = offset.y.value + translation.y.value;

    translation.x.value = 0;
    translation.y.value = 0;
  };

  const getPosition = (i?: number) => {
    "worklet";
    return (
      -(height + emptySpaceHeight) * (typeof i !== "undefined" ? i : index)
    );
  };

  const getIndexFromPosition = (position: number) => {
    "worklet";
    return Math.round(position / -(height + emptySpaceHeight));
  };

  useAnimatedReaction(
    () => {
      return {
        i: currentIndex.value,
        translateY: translateY.value,
        currentScale: scale.value,
      };
    },
    ({ i, translateY, currentScale }) => {
      const translateIndex = translateY / -(height + emptySpaceHeight);
      if (loop) {
        let diff = Math.abs((translateIndex % 1) - 0.5);
        if (diff > 0.5) {
          diff = 1 - diff;
        }
        if (diff > 0.48 && Math.abs(i) !== index) {
          resetValues(false);
          return;
        }
      }
      if (Math.abs(i - index) === 2 && currentScale > 1) {
        resetValues(false);
      }
    }
  );

  useEffect(() => {
    setRef(index, {
      reset: (animated: boolean) => resetValues(animated),
    });
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index]);

  const animatedStyle = useAnimatedStyle(() => {
    const isNextForLast =
      loop &&
      isFirst &&
      currentIndex.value === length - 1 &&
      translateY.value < getPosition(length - 1);
    const isPrevForFirst =
      loop &&
      isLast &&
      currentIndex.value === 0 &&
      translateY.value > getPosition(0);
    return {
      transform: [
        {
          translateY:
            offset.y.value +
            translation.y.value -
            (isNextForLast ? getPosition(length) : 0) +
            (isPrevForFirst ? getPosition(length) : 0),
        },
        { translateX: offset.x.value + translation.x.value },
        { scale: scale.value },
      ],
    };
  });

  const setImageDimensions: RenderItemInfo<T>["setImageDimensions"] = ({
    width: w,
    height: h,
  }) => {
    originalLayout.x.value = w;
    originalLayout.y.value = h;

    const portrait = width > height;

    if (portrait) {
      const imageHeight = Math.min((h * width) / w, height);
      const imageWidth = Math.min(w, width);
      layout.y.value = imageHeight;
      if (imageHeight === height) {
        layout.x.value = (w * height) / h;
      } else {
        layout.x.value = imageWidth;
      }
    } else {
      const imageWidth = Math.min((w * height) / h, width);
      const imageHeight = Math.min(h, height);
      layout.x.value = imageWidth;
      if (imageWidth === width) {
        layout.y.value = (h * width) / w;
      } else {
        layout.y.value = imageHeight;
      }
    }
  };

  useEffect(() => {
    setImageDimensions({
      width: originalLayout.x.value,
      height: originalLayout.y.value,
    });
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [width, height]);

  const itemProps: RenderItemInfo<T> = {
    item,
    index,
    setImageDimensions,
  };

  const scaleOffset = useSharedValue(1);

  const pinchGesture = Gesture.Pinch()
    .enabled(pinchEnabled)
    .onStart(({ focalX, focalY }) => {
      "worklet";
      if (!isActive.value) return;
      onStart();

      scaleOffset.value = scale.value;

      setAdjustedFocal({ focalX, focalY });

      origin.x.value = adjustedFocal.x.value;
      origin.y.value = adjustedFocal.y.value;
    })
    .onUpdate(({ scale: s, focalX, focalY, numberOfPointers }) => {
      "worklet";
      if (!isActive.value) return;
      if (numberOfPointers !== 2) return;

      const nextScale = withRubberBandClamp(
        s * scaleOffset.value,
        0.55,
        maxScale,
        [1, maxScale]
      );

      scale.value = nextScale;

      setAdjustedFocal({ focalX, focalY });

      translation.x.value =
        adjustedFocal.x.value +
        ((-1 * nextScale) / scaleOffset.value) * origin.x.value;
      translation.y.value =
        adjustedFocal.y.value +
        ((-1 * nextScale) / scaleOffset.value) * origin.y.value;
    })
    .onEnd(() => {
      "worklet";
      if (!isActive.value) return;
      if (scale.value < 1) {
        resetValues();
      } else {
        const sc = Math.min(scale.value, maxScale);

        const newWidth = sc * layout.x.value;
        const newHeight = sc * layout.y.value;

        const nextTransX =
          scale.value > maxScale
            ? adjustedFocal.x.value +
              ((-1 * maxScale) / scaleOffset.value) * origin.x.value
            : translation.x.value;

        const nextTransY =
          scale.value > maxScale
            ? adjustedFocal.y.value +
              ((-1 * maxScale) / scaleOffset.value) * origin.y.value
            : translation.y.value;

        const diffX = nextTransX + offset.x.value - (newWidth - width) / 2;

        if (scale.value > maxScale) {
          scale.value = withTiming(maxScale);
        }

        if (newWidth <= width) {
          translation.x.value = withTiming(0);
        } else {
          let moved;
          if (diffX > 0) {
            translation.x.value = withTiming(nextTransX - diffX);
            moved = true;
          }

          if (newWidth + diffX < width) {
            translation.x.value = withTiming(
              nextTransX + width - (newWidth + diffX)
            );
            moved = true;
          }
          if (!moved) {
            translation.x.value = withTiming(nextTransX);
          }
        }

        const diffY = nextTransY + offset.y.value - (newHeight - height) / 2;

        if (newHeight <= height) {
          translation.y.value = withTiming(-offset.y.value);
        } else {
          let moved;
          if (diffY > 0) {
            translation.y.value = withTiming(nextTransY - diffY);
            moved = true;
          }

          if (newHeight + diffY < height) {
            translation.y.value = withTiming(
              nextTransY + height - (newHeight + diffY)
            );
            moved = true;
          }
          if (!moved) {
            translation.y.value = withTiming(nextTransY);
          }
        }
      }
    });

  const isHorizontal = useSharedValue(false);
  const initialTranslateY = useSharedValue(0);
  const isMoving = useVector(0);

  useAnimatedReaction(
    () => currentIndex.value,
    () => resetValues()
  );

  const panGesture = Gesture.Pan()
    .minDistance(10)
    .maxPointers(1)
    .onBegin(() => {
      "worklet";
      if (!isActive.value) return;
      const newWidth = scale.value * layout.x.value;
      const newHeight = scale.value * layout.y.value;
      if (
        offset.x.value < (newWidth - width) / 2 - translation.x.value &&
        offset.x.value > -(newWidth - width) / 2 - translation.x.value
      ) {
        cancelAnimation(offset.x);
      }

      if (
        offset.y.value < (newHeight - height) / 2 - translation.y.value &&
        offset.y.value > -(newHeight - height) / 2 - translation.y.value
      ) {
        cancelAnimation(offset.y);
      }
    })
    .onStart(({ velocityY, velocityX }) => {
      "worklet";
      if (!isActive.value) return;

      onStart();
      isHorizontal.value = Math.abs(velocityX) > Math.abs(velocityY);
      initialTranslateY.value = translateY.value;
    })
    .onUpdate(({ translationX, translationY }) => {
      "worklet";
      if (!isActive.value) return;
      if (scale.value === 1 && isHorizontal.value) return;

      if (!isHorizontal.value || scale.value > 1) {
        const y = getEdgeY();
        const clampedY = clamp(
          translationY,
          y[0] - offset.y.value,
          y[1] - offset.y.value
        );

        const transY = initialTranslateY.value + translationY - clampedY;

        if (hideAdjacentImagesOnScaledImage && disableTransitionOnScaledImage) {
          const disabledTransition =
            disableTransitionOnScaledImage && scale.value > 1;

          const moveY = withRubberBandClamp(
            transY,
            0.55,
            height,
            disabledTransition
              ? [getPosition(index), getPosition(index + 1)]
              : [getPosition(length - 1), 0]
          );

          if (!disabledTransition) {
            translateY.value = moveY;
          }
          if (disabledTransition) {
            translation.y.value = clampedY + moveY - translateY.value;
          } else {
            translation.y.value = clampedY;
          }
        } else {
          if (loop) {
            translateY.value = transY;
          } else {
            translateY.value = withRubberBandClamp(
              transY,
              0.55,
              height,
              disableTransitionOnScaledImage && scale.value > 1
                ? [getPosition(index), getPosition(index + 1)]
                : [getPosition(length - 1), 0]
            );
          }
          translation.y.value = clampedY;
        }
      }

      const newWidth = scale.value * layout.x.value;

      const edgeX = getEdgeX();

      if (newWidth > width) {
        translation.x.value = withRubberBandClamp(translationX, 0.9, newWidth, [
          edgeX[0] - offset.x.value,
          edgeX[1] - offset.x.value,
        ]);
      } else if (
        !(scale.value === 1 && translateY.value !== getPosition()) &&
        translationX >= 0
      ) {
        translation.x.value = translationX;
      }
    })
    .onEnd(({ velocityY, velocityX }) => {
      "worklet";
      if (!isActive.value) return;

      const newWidth = scale.value * layout.x.value;

      const edgeY = getEdgeY();

      if (
        Math.abs(translateY.value - getPosition()) >= 0 &&
        edgeY.some((y) => y === translation.y.value + offset.y.value)
      ) {
        let snapPoints = [index - 1, index, index + 1]
          .filter((_, y) => {
            if (loop) return true;

            if (y === 0) {
              return !isFirst;
            }
            if (y === 2) {
              return !isLast;
            }
            return true;
          })
          .map((i) => getPosition(i));

        if (disableTransitionOnScaledImage && scale.value > 1) {
          snapPoints = [getPosition(index)];
        }

        let snapTo = snapPoint(translateY.value, velocityY, snapPoints);

        const nextIndex = getIndexFromPosition(snapTo);

        if (currentIndex.value !== nextIndex) {
          if (loop) {
            if (nextIndex === length) {
              currentIndex.value = 0;
              translateY.value = translateY.value - getPosition(length);
              snapTo = 0;
            } else if (nextIndex === -1) {
              currentIndex.value = length - 1;
              translateY.value = translateY.value + getPosition(length);
              snapTo = getPosition(length - 1);
            } else {
              currentIndex.value = nextIndex;
            }
          } else {
            currentIndex.value = nextIndex;
          }
        }

        translateY.value = withSpring(snapTo, {
          damping: 800,
          mass: 1,
          stiffness: 250,
        });
      } else {
        const newHeight = scale.value * layout.y.value;

        const v = withDecaySpring(
          {
            velocity: velocityY,
            clamp: [
              -(newHeight - height) / 2 - translation.y.value,
              (newHeight - height) / 2 - translation.y.value,
            ],
          },
          (isFinished) => {
            "worklet";
            isMoving.y.value = 0;
            if (isFinished) {
              isMoving.x.value = 0;
            }
          }
        );
        isMoving.y.value = 1;
        offset.y.value = v;
      }

      if (newWidth > width) {
        isMoving.x.value = 1;
        offset.x.value = withDecaySpring(
          {
            velocity: velocityX,
            clamp: [
              -(newWidth - width) / 2 - translation.x.value,
              (newWidth - width) / 2 - translation.x.value,
            ],
          },
          (isFinished) => {
            "worklet";
            isMoving.x.value = 0;
            if (isFinished) {
              isMoving.y.value = 0;
            }
          }
        );
      } else {
        const diffX =
          translation.x.value + offset.x.value - (newWidth - width) / 2;

        if (newWidth <= width && diffX !== width - diffX - newWidth) {
          const moveTo = diffX - (width - newWidth) / 2;

          translation.x.value = withTiming(translation.x.value - moveTo);
        }
      }
    });

  const doubleTapGesture = Gesture.Tap()
    .numberOfTaps(2)
    .maxDelay(doubleTapInterval)
    .onEnd(({ x, y, numberOfPointers }) => {
      "worklet";
      if (!isActive.value) return;
      if (numberOfPointers !== 1) return;

      if (scale.value === 1) {
        scale.value = withTiming(doubleTapScale);

        setAdjustedFocal({ focalX: x, focalY: y });

        offset.x.value = withTiming(
          clampX(
            adjustedFocal.x.value + -1 * doubleTapScale * adjustedFocal.x.value,
            doubleTapScale
          )
        );
        offset.y.value = withTiming(
          clampY(
            adjustedFocal.y.value + -1 * doubleTapScale * adjustedFocal.y.value,
            doubleTapScale
          )
        );
      } else {
        resetValues();
      }
    });

  return (
    <GestureDetector
      gesture={Gesture.Race(
        Gesture.Race(panGesture, pinchGesture),
        doubleTapGesture
      )}
    >
      <View style={{ width, height }}>
        <Animated.View style={[{ width, height }, animatedStyle]}>
          {renderItem(itemProps)}
        </Animated.View>
      </View>
    </GestureDetector>
  );
};
